import { ChangeEvent, useState, useEffect, FC } from "react";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import $api from "../../shared/api/axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getRouteDashboard, getRouteSignIn } from "../../shared/const/router";
import { FigmaTypography } from "@libs/zapi-ui/custom";

export const VerifyOtpPage: FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [otpCode, setOtpCode] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const twoFactorToken = new URLSearchParams(search).get("token");

  useEffect(() => {
    if (!twoFactorToken) {
      navigate(getRouteSignIn());
    }
  }, [twoFactorToken, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const payload = { token: twoFactorToken, otpCode };
      return await $api.post("/auth/verify-otp", payload);
    },
    onSuccess: () => {
      const next = new URLSearchParams(search).get("next");
      if (next) {
        navigate(next);
        return;
      }
      navigate(getRouteDashboard());
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to verify code";
      toast.error(message);
    }
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      setIsResending(true);
      const payload = { token: twoFactorToken };
      return await $api.post("/auth/resend-otp", payload);
    },
    onSuccess: () => {
      toast.success("Verification code resent to your email");
      setCountdown(60);
      setIsResending(false);
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to resend code";
      toast.error(message);
      setIsResending(false);
      if (err?.response?.status === 401) {
        navigate(getRouteSignIn());
      }
    }
  });

  function onChangeOtp(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
  }

  async function verifyOtp() {
    if (otpCode.length !== 6) return;
    try {
      await verifyMutation.mutateAsync();
    } catch {
      return;
    }
  }

  async function resendOtp() {
    if (countdown > 0 || isResending) return;
    try {
      await resendMutation.mutateAsync();
    } catch {
      return;
    }
  }

  const isValidOtp = otpCode.length === 6;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <FigmaTypography component="h1" variant="memo-heading-1">Enter Verification Code</FigmaTypography>
          <p className="text-[14px] text-muted-foreground">
            We sent a 6-digit verification code to your email. Please enter it below.
          </p>
        </div>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <label htmlFor="otp" className="text-[14px] font-medium leading-none">
              Verification Code
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="h-12 rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-[0.5em] font-mono ring-offset-background focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
              value={otpCode}
              onChange={onChangeOtp}
              placeholder="000000"
              maxLength={6}
            />
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={resendOtp}
              disabled={countdown > 0 || isResending}
              className="text-[14px] text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
            </button>
          </div>

          <button
            className={`h-10 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 ${
              !isValidOtp || verifyMutation.isPending ? "cursor-not-allowed opacity-70" : ""
            }`}
            disabled={!isValidOtp || verifyMutation.isPending}
            onClick={verifyOtp}
          >
            {verifyMutation.isPending ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
};
